/**
 * layout = a list of layoutItems, that make a react-grid-layout
 * matrix - a conversion of layout => array[rows][cols] - for processing
 * widget - layoutitem representing the actual widget

 */

import cloneDeep from "lodash/cloneDeep";

/**
 *
 * @param cols
 * @param rows
 */
function createGrid(cols: number, rows: number) {
    return Array(rows)
        .fill(null)
        .map(() => Array(cols).fill(undefined));
}

/**
 *
 * @param layout
 */
function findLastRowFromLayout(layout: FixmeType) {
    let max = 0,
        bottomY;
    for (let i = 0, len = layout.length; i < len; i++) {
        bottomY = layout[i].y + layout[i].h;
        if (bottomY > max) {
            max = bottomY;
        }
    }
    return max;
}

/**
 *
 * @param layout
 */
function findLastRowFromWidgets(widgets: FixmeType) {
    const layout = translateWidgetsToLayout(widgets);
    return findLastRowFromLayout(layout);
}

/**
 *
 * @param matrix
 * @param layoutItem
 */
function translateLayoutItemToMatrix(matrix: FixmeType, layoutItem: FixmeType) {
    for (let x = layoutItem.x; x < layoutItem.x + layoutItem.w; x++) {
        for (let y = layoutItem.y; y < layoutItem.y + layoutItem.h; y++) {
            matrix[y][x] = layoutItem.i;
        }
    }

    return matrix;
}

/**
 * Translate widget to layoutItem
 * @param widget
 * @param isStatic s
 */
function getWidgetLayoutData(widget: FixmeType, isStatic?: boolean) {
    const { col, row, sizeX, minSizeX, maxSizeX, sizeY } = widget.pos;
    const resizeHandles = minSizeX === maxSizeX ? [] : ["e"];
    const layoutData = {
        i: widget.id || widget.pos,
        x: col,
        y: row,
        w: sizeX || 1,
        minW: minSizeX || sizeX,
        maxW: maxSizeX || 4,
        isResizable: minSizeX === maxSizeX ? false : true,
        h: sizeY || 1,
        resizeHandles: resizeHandles,
        static: isStatic,
    };
    return layoutData;
}
/**
 * Move between Widgets and Layout
 * @param widgets
 * @param isSharedWithMe
 */
function translateWidgetsToLayout(widgets: FixmeType, isStatic?: boolean) {
    const newWidgetLayoutItems = [];
    const layout = [];

    for (let i = 0; i < widgets.length; i++) {
        const widget = widgets[i];
        const layoutData = getWidgetLayoutData(widget, isStatic);
        if (layoutData.x === undefined || layoutData.y === undefined) {
            newWidgetLayoutItems.push(layoutData);
        } else {
            layout.push(layoutData);
        }
    }
    for (let i = 0; i < newWidgetLayoutItems.length; i++) {
        addLayoutItem(layout, newWidgetLayoutItems[i]);
    }
    if (isStatic) {
        for (let i = 0; i < layout.length; i++) {
            layout[i].static = true;
        }
    }

    return layout;
}

function translateLayoutToMatrix(layout: FixmeType) {
    const btm = findLastRowFromLayout(layout);
    const grid = createGrid(4, btm);
    layout.forEach((layoutItem: FixmeType) => {
        translateLayoutItemToMatrix(grid, layoutItem);
        // console.log("new layout", layout);
    });
    // console.log(layout);
    return grid;
}

/**
 * We build a widget from layout , but the id is pos
 * @param layout
 * @param widgets
 */
function buildWidgetsFromLayout(layout: FixmeType, widgets: FixmeType) {
    return layout.map((li: FixmeType, index: number) => {
        const w = widgets[index];
        return {
            ...w,
            pos: {
                ...w.pos,
                col: li.x,
                row: li.y,
            },
        };
    });
}

function buildLayoutFromMatrix(matrix: FixmeType, layout: FixmeType) {
    const newLayout = new Map();
    for (let row = 0; row < matrix.length; row++) {
        const widths = new Map();
        for (let col = 0; col < matrix[row].length; col++) {
            const currentWidgetFromMatrix = matrix[row][col];
            if (!currentWidgetFromMatrix) {
                continue;
            }

            const curr = newLayout.get(currentWidgetFromMatrix);
            //if the widget was not encountered before
            if (!curr) {
                newLayout.set(currentWidgetFromMatrix, {
                    x: col,
                    y: row,
                    w: 1,
                    h: 1,
                });
                widths.set(currentWidgetFromMatrix, 1);
            } else {
                if (row === curr.y) {
                    widths.set(currentWidgetFromMatrix, widths.get(currentWidgetFromMatrix) + 1);
                } else {
                    if (row !== curr.y + curr.h - 1) {
                        curr.h = curr.h + 1;
                        newLayout.set(currentWidgetFromMatrix, curr);
                    }
                }
            }
        }
        //now update widths
        widths.forEach((value, key) => {
            const newLayI = newLayout.get(key);
            newLayI.w = value;
        });
    }

    const clonedLayout = [];
    for (let i = 0; i < layout.length; i++) {
        const w = layout[i];
        const prepared = newLayout.get(w.i) || newLayout.get(w.pos);
        if (!prepared) {
            continue;
        }
        clonedLayout.push({
            ...w,
            x: prepared.x,
            y: prepared.y,
            w: prepared.w,
            h: prepared.h,
        });
    }

    // console.log(clonedLayout);
    return clonedLayout;
}

function canFit(x: number, y: number, w: number, h: number, matrix: FixmeType) {
    if (!matrix || matrix.length < y + h || matrix[0].length < x + w) {
        return false;
    }

    for (let row = y; row < y + h; row++) {
        for (let col = x; col < x + w; col++) {
            if (matrix[row][col] !== undefined) {
                return false;
            }
        }
    }

    return true;
}

function fitLayoutItem(layout: FixmeType, layoutItem: FixmeType) {
    const matrix = translateLayoutToMatrix(layout);
    //iterate over matrix, find a spot
    for (let row = 0; row < matrix.length - layoutItem.h + 1; row++) {
        for (let col = 0; col < matrix[row].length - layoutItem.w + 1; col++) {
            const fits = canFit(col, row, layoutItem.w, layoutItem.h, matrix);
            if (fits) {
                layoutItem.x = col;
                layoutItem.y = row;
                translateLayoutItemToMatrix(matrix, layoutItem);
                layout.push(layoutItem);
                return buildLayoutFromMatrix(matrix, layout);
            }
        }
    }

    //no place found so add in new line
    layoutItem.x = 0;
    layoutItem.y = matrix.length;
    for (let y = 0; y < layoutItem.h; y++) {
        matrix.push(Array(4).fill(undefined));
    }
    translateLayoutItemToMatrix(matrix, layoutItem);
    // console.table(matrix);
    layout.push(layoutItem);
    return buildLayoutFromMatrix(matrix, layout);
}

/**
 * @param widget {id, pos}
 * @return {x,y,w,h}
 */
function translateWidgetToLayoutItem(widget: FixmeType) {
    return {
        i: widget.pos,
        x: widget.pos.col,
        y: widget.pos.row,
        w: widget.pos.sizeX,
        h: widget.pos.sizeY,
    };
}

/**
 * used only to create a layout without ids
 * @param widget
 * @param widgets
 * @param deltaRow add this to widget row, if we get widget row. since this is in use with templates and we need to adjust for number of keys per template(which can create multiple extra rows)
 */
function putItem(widget: FixmeType, widgets: FixmeType, deltaRow = 0) {
    const layout = translateWidgetsToLayout(widgets);
    const { pos } = widget;
    const { row } = pos;
    if (typeof row === "undefined" || row === null) {
        fitLayoutItem(layout, { i: pos, w: pos.sizeX || 1, h: pos.sizeY || 1 });
    } else {
        widget.pos.row += deltaRow;
        const li = translateWidgetToLayoutItem(widget);
        // @ts-ignore FIXME: strict migration - layoutItem shape missing minW/maxW/isResizable/resizeHandles/static — layout's element type is inferred from getWidgetLayoutData while translateWidgetToLayoutItem returns a leaner shape; both are untyped legacy grid items
        layout.push(li);
    }
    widgets.push(widget);
    return buildWidgetsFromLayout(layout, widgets);
}

function addLayoutItem(layout: FixmeType, layoutItem: FixmeType) {
    const matrix = translateLayoutToMatrix(layout);
    layoutItem.x = 0;
    layoutItem.y = matrix.length;
    for (let x = 0; x < layoutItem.h; x++) {
        matrix.push(Array(4).fill(undefined));
    }
    translateLayoutItemToMatrix(matrix, layoutItem);
    // console.table(matrix);
    layout.push(layoutItem);
    return buildLayoutFromMatrix(matrix, layout);
}

function enlarge(widgetId: FixmeType, layout: FixmeType, shouldEnlarge: boolean) {
    const clonedLayout = cloneDeep(layout);
    const widget = clonedLayout.find((w: FixmeType) => w.i === widgetId);
    let matrix;
    if (shouldEnlarge) {
        matrix = translateLayoutToMatrix(clonedLayout);
        //add the new window(row)
        matrix.splice(widget.y + 1, 0, [undefined, undefined, undefined, undefined]);
        //check if added window is not at the end
        if (widget.y + 2 !== matrix.length) {
            //iterate over newRow
            for (let x = 0; x < matrix[widget.y + 1].length; x++) {
                const oldRow = matrix[widget.y];
                const newRow = matrix[widget.y + 1];
                const pushedRow = matrix[widget.y + 2];
                if (pushedRow[x] !== undefined && pushedRow[x] === oldRow[x] && pushedRow[x] !== widget.i) {
                    newRow[x] = pushedRow[x];
                    pushedRow[x] = undefined;
                }
            }
        }
        widget.h = 2;
        translateLayoutItemToMatrix(matrix, widget);
    } else {
        widget.h = 1;
        matrix = translateLayoutToMatrix(clonedLayout);
        // console.table(matrix);
        // translateLayoutItemToMatrix(matrix, widget);
    }
    removeEmptyLines(matrix);
    return buildLayoutFromMatrix(matrix, clonedLayout);
}

function removeEmptyLines(matrix: FixmeType) {
    let i = 0;
    while (matrix.length > i) {
        const line = matrix[i];
        const emptyLine = line.every((item: FixmeType) => !item);
        if (emptyLine) {
            matrix.splice(i, 1);
        } else {
            i++;
        }
    }
    return matrix;
}

function removeEmptyLinesFromLayout(layout: FixmeType) {
    const clonedLayout = cloneDeep(layout);
    let matrix = translateLayoutToMatrix(clonedLayout);
    matrix = removeEmptyLines(matrix);
    const newLayout = buildLayoutFromMatrix(matrix, clonedLayout);
    return newLayout;
}

export {
    findLastRowFromWidgets,
    removeEmptyLinesFromLayout,
    enlarge,
    translateWidgetsToLayout,
    putItem,
    buildWidgetsFromLayout,
};
